import {
  HHAlert,
  HHButton,
  HHLoadingButton,
  HHStack,
  HHTextField,
  HHTypography,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputAdornment } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDynamicDrawerContext } from '../../../billing-tool/context/dynamic-drawer.context';
import { ActiveStatus } from '../../../billing-tool/utils/contract-utils';
import {
  formatCurrency,
  getCurrencySymbol,
} from '../../../billing-tool/utils/currency-helpers';
import {
  SUBSCRIPTION_ESTIMATE_FORM_CANCEL_TEST_ID,
  SUBSCRIPTION_ESTIMATE_FORM_END_DATE_INPUT_TEST_ID,
  SUBSCRIPTION_ESTIMATE_FORM_GROSS_INPUT_TEST_ID,
  SUBSCRIPTION_ESTIMATE_FORM_NET_DISPLAY_TEST_ID,
  SUBSCRIPTION_ESTIMATE_FORM_PERIOD_ERROR_TEST_ID,
  SUBSCRIPTION_ESTIMATE_FORM_RESERVE_INPUT_TEST_ID,
  SUBSCRIPTION_ESTIMATE_FORM_START_DATE_INPUT_TEST_ID,
  SUBSCRIPTION_ESTIMATE_FORM_SUBMIT_TEST_ID,
  SUBSCRIPTION_ESTIMATE_FORM_TEST_ID,
} from '../subscription-estimates-grid/types';
import { checkEstimatePeriod, EstimatePeriod } from './period-validation';
import {
  DEFAULT_SUBSCRIPTION_ESTIMATE_FORM_VALUES,
  SubscriptionEstimateFormValues,
  SubscriptionEstimateValidationSchema,
} from './validation-schema';

const OVERLAP_ERROR = 'This period overlaps an existing estimate.';
const GAP_ERROR =
  'This period must be contiguous with the existing estimates. Gaps are not allowed.';

const GROSS_HELPER_TEXT =
  'Full per-period booking amount for the engagement subscription.';
const RESERVE_HELPER_TEXT =
  'Contra-revenue held back from the gross. Enter a positive amount - it reduces the net.';
const NET_HELPER_TEXT = 'The net is what NetSuite recognizes against.';
const START_DATE_HELPER_TEXT = 'When this value starts being sent to NetSuite.';
const END_DATE_HELPER_TEXT = 'When this value stops being sent to NetSuite.';

interface SubscriptionEstimateFormProps {
  onSubmit: (values: SubscriptionEstimateFormValues) => void | Promise<void>;
  initialValues?: SubscriptionEstimateFormValues;
  /** Edit target id. Excluded from cross-record validation and passed through. */
  id?: number;
  /** Row status, drives the edit-mode field-enable matrix. */
  status?: ActiveStatus;
  /**
   * The other existing estimates to validate the candidate period against
   * (overlap and gap are both hard blocks). The record being edited is excluded
   * by `id`.
   */
  existingEstimates?: EstimatePeriod[];
}

// Parses a numeric text-input value to number, mapping empty/NaN to null so the
// currency fields feed number | null into the schema.
const parseCurrencyInput = (raw: string): number | null => {
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

export const SubscriptionEstimateForm = ({
  onSubmit,
  initialValues,
  id,
  status,
  existingEstimates = [],
}: SubscriptionEstimateFormProps): JSX.Element => {
  const { onClose } = useDynamicDrawerContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [periodError, setPeriodError] = useState<string | null>(null);

  const methods = useForm<SubscriptionEstimateFormValues>({
    mode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(SubscriptionEstimateValidationSchema),
    defaultValues: initialValues ?? DEFAULT_SUBSCRIPTION_ESTIMATE_FORM_VALUES,
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
  } = methods;

  useEffect(() => {
    if (initialValues) {
      reset(initialValues);
    }
  }, [initialValues, reset]);

  const isEditMode = !!initialValues;

  // Field-enable matrix. Add mode: everything editable. Edit mode: startDate is
  // always read-only (UpdateSubscriptionEstimateInput has no startDate); Gross,
  // Reserve and End stay editable (Active values change mid-quarter for future
  // Sales Orders, and Past rows never reach the edit drawer). Net always ro.
  const startDateDisabled = isEditMode;
  const endDateDisabled = isEditMode && status === ActiveStatus.Past;

  const [grossAmount, reserveAmount] = watch(['grossAmount', 'reserveAmount']);
  const netDisplay = formatCurrency(
    (grossAmount ?? 0) - (reserveAmount ?? 0),
    'USD',
  );

  const handleCancel = (): void => {
    reset();
    onClose();
  };

  const submitValues = async (
    values: SubscriptionEstimateFormValues,
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = async (
    values: SubscriptionEstimateFormValues,
  ): Promise<void> => {
    setPeriodError(null);
    const { hasOverlap, hasGap } = checkEstimatePeriod(
      { startDate: values.startDate, endDate: values.endDate },
      existingEstimates,
      id,
    );

    if (hasOverlap) {
      setPeriodError(OVERLAP_ERROR);
      return;
    }

    if (hasGap) {
      setPeriodError(GAP_ERROR);
      return;
    }

    await submitValues(values);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        data-testid={SUBSCRIPTION_ESTIMATE_FORM_TEST_ID}
      >
        <HHStack spacing={6} direction="column">
          {periodError && (
            <HHAlert severity="error" hhVariant="outlined">
              <div
                data-testid={SUBSCRIPTION_ESTIMATE_FORM_PERIOD_ERROR_TEST_ID}
              >
                <HHTypography hhVariant="body">{periodError}</HHTypography>
              </div>
            </HHAlert>
          )}

          <Controller
            control={control}
            name="grossAmount"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHTextField
                hhVariant="variant-bypass"
                variant="outlined"
                label="Gross Amount"
                type="number"
                value={value ?? ''}
                onChange={(e): void =>
                  onChange(parseCurrencyInput(e.target.value))
                }
                error={!!errors.grossAmount}
                helperText={errors.grossAmount?.message ?? GROSS_HELPER_TEXT}
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      disableTypography
                      sx={{ color: 'gray' }}
                    >
                      {getCurrencySymbol('USD')}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: 'decimal',
                  step: '0.01',
                  min: '0',
                  name: 'grossAmount',
                  role: 'textbox',
                }}
                data-testid={SUBSCRIPTION_ESTIMATE_FORM_GROSS_INPUT_TEST_ID}
              />
            )}
          />

          <Controller
            control={control}
            name="reserveAmount"
            render={({ field: { onChange, value } }): JSX.Element => (
              <HHTextField
                hhVariant="variant-bypass"
                variant="outlined"
                label="Reserve Amount"
                type="number"
                value={value ?? ''}
                onChange={(e): void =>
                  onChange(parseCurrencyInput(e.target.value))
                }
                error={!!errors.reserveAmount}
                helperText={
                  errors.reserveAmount?.message ?? RESERVE_HELPER_TEXT
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      disableTypography
                      sx={{ color: 'gray' }}
                    >
                      {getCurrencySymbol('USD')}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  inputMode: 'decimal',
                  step: '0.01',
                  min: '0',
                  name: 'reserveAmount',
                  role: 'textbox',
                }}
                data-testid={SUBSCRIPTION_ESTIMATE_FORM_RESERVE_INPUT_TEST_ID}
              />
            )}
          />

          <HHTextField
            hhVariant="variant-bypass"
            variant="outlined"
            label="Net Booking"
            value={netDisplay}
            disabled
            InputProps={{ readOnly: true }}
            helperText={NET_HELPER_TEXT}
            data-testid={SUBSCRIPTION_ESTIMATE_FORM_NET_DISPLAY_TEST_ID}
          />

          <Controller
            control={control}
            name="startDate"
            render={({ field: { onChange, value } }): JSX.Element => (
              <DatePicker
                label="Start Date"
                value={value}
                disabled={startDateDisabled}
                onChange={(newValue): void =>
                  onChange(newValue ? dayjs(newValue).toDate() : null)
                }
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    hhVariant="variant-bypass"
                    name="startDate"
                    fullWidth
                    error={params.error || !!errors.startDate}
                    helperText={
                      errors.startDate?.message ?? START_DATE_HELPER_TEXT
                    }
                    data-testid={
                      SUBSCRIPTION_ESTIMATE_FORM_START_DATE_INPUT_TEST_ID
                    }
                  />
                )}
              />
            )}
          />

          <Controller
            control={control}
            name="endDate"
            render={({ field: { onChange, value } }): JSX.Element => (
              <DatePicker
                label="End Date (optional)"
                value={value}
                disabled={endDateDisabled}
                onChange={(newValue): void =>
                  onChange(newValue ? dayjs(newValue).toDate() : null)
                }
                renderInput={(params): JSX.Element => (
                  <HHTextField
                    {...params}
                    hhVariant="variant-bypass"
                    name="endDate"
                    fullWidth
                    error={params.error || !!errors.endDate}
                    helperText={errors.endDate?.message ?? END_DATE_HELPER_TEXT}
                    data-testid={
                      SUBSCRIPTION_ESTIMATE_FORM_END_DATE_INPUT_TEST_ID
                    }
                  />
                )}
              />
            )}
          />

          <HHStack direction="row" spacing={2} justifyContent="flex-end">
            <HHButton
              hhVariant="variant-bypass"
              variant="text"
              onClick={handleCancel}
              type="button"
              disabled={isSubmitting}
              data-testid={SUBSCRIPTION_ESTIMATE_FORM_CANCEL_TEST_ID}
            >
              Cancel
            </HHButton>
            <HHLoadingButton
              hhVariant="variant-bypass"
              variant="contained"
              type="submit"
              disabled={!isValid || !isDirty || isSubmitting}
              loading={isSubmitting}
              data-testid={SUBSCRIPTION_ESTIMATE_FORM_SUBMIT_TEST_ID}
            >
              {isEditMode ? 'Save' : 'Create'}
            </HHLoadingButton>
          </HHStack>
        </HHStack>
      </form>
    </FormProvider>
  );
};
